import { useCallback, useEffect, useState } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { VmScheduleEditor, VmScheduleView } from './VmScheduleEditor';

export type VmPowerState = 'running' | 'starting' | 'stopping' | 'stopped' | 'unknown';

export interface EnvironmentVm {
  key: string;
  label: string;
  powerState: VmPowerState;
}

/** How often the panel re-reads power state while a VM is starting or stopping. */
export const VM_POLL_INTERVAL_MS = 5_000;

const STATE_LABEL: Record<VmPowerState, string> = {
  running: 'Running',
  starting: 'Starting',
  stopping: 'Stopping',
  stopped: 'Stopped',
  unknown: 'Unknown',
};

const STATE_COLOR: Record<VmPowerState, string> = {
  running: '#2e7d32',
  starting: '#1976d2',
  stopping: '#ef6c00',
  stopped: '#616161',
  unknown: '#9e9e9e',
};

type Action = 'start' | 'stop';

/**
 * The VMs of a ready environment with their power state and Start/Stop buttons. An environment
 * with more than one VM (Demo has a host and an optional portal) also gets Start all and Stop all.
 * Renders nothing when the backend reports no VMs.
 */
export function EnvironmentVmPanel({ environmentName }: { environmentName: string }) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [vms, setVms] = useState<EnvironmentVm[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [schedules, setSchedules] = useState<VmScheduleView[]>([]);

  const baseUrl = useCallback(
    async () => `${await discoveryApi.getBaseUrl('environment-status')}/${encodeURIComponent(environmentName)}`,
    [discoveryApi, environmentName],
  );

  const load = useCallback(async () => {
    try {
      const response = await fetchApi.fetch(`${await baseUrl()}/vms`);
      if (!response.ok) {
        throw new Error(`Failed to read the VMs: ${response.status}`);
      }
      const body = await response.json();
      if (Array.isArray(body?.vms)) {
        setVms(body.vms);
        setError(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [fetchApi, baseUrl]);

  // Schedules are optional: a backend without them (501) or a read failure just shows none.
  const loadSchedules = useCallback(async () => {
    try {
      const response = await fetchApi.fetch(`${await baseUrl()}/schedules`);
      if (response.ok) {
        const body = await response.json();
        setSchedules(Array.isArray(body?.schedules) ? body.schedules : []);
      }
    } catch {
      setSchedules([]);
    }
  }, [fetchApi, baseUrl]);

  useEffect(() => {
    load();
    loadSchedules();
  }, [load, loadSchedules]);

  async function saveSchedule(vmKey: string, input: Omit<VmScheduleView, 'vmKey'>) {
    const response = await fetchApi.fetch(`${await baseUrl()}/schedules/${vmKey}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => undefined);
      throw new Error(body?.message ?? `Failed to save the schedule: ${response.status}`);
    }
    const saved: VmScheduleView = await response.json();
    setSchedules(current => [...current.filter(schedule => schedule.vmKey !== vmKey), saved]);
  }

  async function clearSchedule(vmKey: string) {
    const response = await fetchApi.fetch(`${await baseUrl()}/schedules/${vmKey}`, { method: 'DELETE' });
    if (!response.ok) {
      throw new Error(`Failed to clear the schedule: ${response.status}`);
    }
    setSchedules(current => current.filter(schedule => schedule.vmKey !== vmKey));
  }

  const inTransition = Boolean(vms?.some(vm => vm.powerState === 'starting' || vm.powerState === 'stopping'));
  useEffect(() => {
    if (!inTransition) {
      return undefined;
    }
    const timer = setInterval(load, VM_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [inTransition, load]);

  async function run(path: string, action: Action, affected: string[]) {
    setError(null);
    setBusy(true);
    try {
      const response = await fetchApi.fetch(`${await baseUrl()}/${path}/${action}`, { method: 'POST' });
      if (!response.ok) {
        const body = await response.json().catch(() => undefined);
        throw new Error(body?.message ?? `Failed to ${action}: ${response.status}`);
      }
      // Show the change at once; the poll then follows Azure until it settles.
      const next: VmPowerState = action === 'start' ? 'starting' : 'stopping';
      setVms(current =>
        current ? current.map(vm => (affected.includes(vm.key) ? { ...vm, powerState: next } : vm)) : current,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  function confirmStop(what: string) {
    return window.confirm(`Stop ${what}? Anything running on it will be interrupted. You can start it again here.`);
  }

  if (!vms || vms.length === 0) {
    return error ? <p>{error}</p> : null;
  }

  const stoppable = vms.filter(vm => vm.powerState === 'running');
  const startable = vms.filter(vm => vm.powerState === 'stopped');

  return (
    <div data-testid="environment-vms">
      <h4>Virtual machines</h4>
      <ul style={{ paddingLeft: 0, listStyle: 'none' }}>
        {vms.map(vm => (
          <li key={vm.key} data-testid={`environment-vm-${vm.key}`} style={{ marginBottom: 4 }}>
            {vms.length > 1 ? `${vm.label}: ` : ''}
            <span data-testid={`environment-vm-${vm.key}-state`} style={{ color: STATE_COLOR[vm.powerState], fontWeight: 'bold' }}>
              {STATE_LABEL[vm.powerState]}
            </span>{' '}
            <button
              type="button"
              disabled={busy || vm.powerState !== 'stopped'}
              onClick={() => run(`vms/${vm.key}`, 'start', [vm.key])}
              style={{ marginRight: 4 }}
            >
              Start
            </button>
            <button
              type="button"
              disabled={busy || vm.powerState !== 'running'}
              onClick={() => confirmStop(vms.length > 1 ? `the ${vm.label} VM` : 'the VM') && run(`vms/${vm.key}`, 'stop', [vm.key])}
            >
              Stop
            </button>
            <VmScheduleEditor
              vmKey={vm.key}
              vmLabel={vm.label}
              schedule={schedules.find(schedule => schedule.vmKey === vm.key)}
              onSave={input => saveSchedule(vm.key, input)}
              onClear={() => clearSchedule(vm.key)}
            />
          </li>
        ))}
      </ul>
      {vms.length > 1 && (
        <div>
          <button
            type="button"
            disabled={busy || startable.length === 0}
            onClick={() => run('power', 'start', startable.map(vm => vm.key))}
            style={{ marginRight: 4 }}
          >
            Start all
          </button>
          <button
            type="button"
            disabled={busy || stoppable.length === 0}
            onClick={() => confirmStop('all the VMs') && run('power', 'stop', stoppable.map(vm => vm.key))}
          >
            Stop all
          </button>
        </div>
      )}
      {error && <p>{error}</p>}
    </div>
  );
}
